import { useState } from 'react';
import Modal from '../components/Modal.jsx';
import { useConfirm } from '../components/ConfirmDialog.jsx';
import { fmtCents, parseAmountToCents, todayIso } from '../format.js';
import { api } from '../api.js';

export default function Settings({ state, lookups, generic, mutate, showToast, reload }) {
  const [editingCat, setEditingCat] = useState(null);
  const [editingAcct, setEditingAcct] = useState(null);
  const [savingSetting, setSavingSetting] = useState(null);

  const cats = state.categories.filter(c => !c.archived).sort((a,b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const accts = state.accounts.filter(a => !a.archived).sort((a,b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  async function saveSetting(key, value) {
    setSavingSetting(key);
    try {
      await api.setSetting(key, value);
      await reload();
      showToast({ kind: 'success', message: 'Saved.' });
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally { setSavingSetting(null); }
  }

  return (
    <div className="screen">
      <h1>Settings</h1>

      {/* Reconcile Checking — replaces the dead "Starting checking balance"
          card. Posts a single Income/Expense transaction on today's date
          to nudge the ledger to match the bank, instead of overwriting
          a stored balance. Append-only and reversible: deleting the
          adjustment transaction undoes the reconciliation. */}
      <h2>Reconcile checking</h2>
      <div className="card">
        <ReconcileChecking state={state} mutate={mutate} reload={reload} showToast={showToast} />
      </div>

      {/* App settings */}
      <h2>App settings</h2>
      <div className="card">
        <SettingRow
          label="Weekly discretionary budget"
          value={fmtCents(parseInt(lookups.setting('weekly_disc_budget_cents') ?? '0', 10))}
          editValue={String(parseInt(lookups.setting('weekly_disc_budget_cents') ?? '0', 10) / 100)}
          parse={(v) => String(parseAmountToCents(v) ?? 0)}
          onSave={(v) => saveSetting('weekly_disc_budget_cents', v)}
          saving={savingSetting === 'weekly_disc_budget_cents'}
        />
        <SettingRow
          label="Discretionary categories"
          value={lookups.setting('disc_categories') ?? '—'}
          editValue={lookups.setting('disc_categories') ?? ''}
          onSave={(v) => saveSetting('disc_categories', v)}
          saving={savingSetting === 'disc_categories'}
          hint="Comma-separated list of category names."
        />
        <SettingRow
          label="Cushion floor"
          value={fmtCents(parseInt(lookups.setting('cushion_floor_cents') ?? '0', 10))}
          editValue={String(parseInt(lookups.setting('cushion_floor_cents') ?? '0', 10) / 100)}
          parse={(v) => String(parseAmountToCents(v) ?? 0)}
          onSave={(v) => saveSetting('cushion_floor_cents', v)}
          saving={savingSetting === 'cushion_floor_cents'}
          hint="Forecast warns when projected balance falls below this."
          last
        />
        {/* "Starting checking balance" hidden 2026-05-08. The dashboard is a
            pure ledger sum and ignores this value; editing it reset nothing.
            Replaced by the Reconcile checking card above. The setting row
            stays in D1 in case we ever want to surface it again. */}
      </div>

      {/* Categories */}
      <div className="row between" style={{ marginTop: 24, marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Categories</h2>
        <button onClick={() => setEditingCat('new')}>+ New</button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        {cats.map(c => (
          <button key={c.id} onClick={() => setEditingCat(c)} className="list-item"
                  style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                           borderBottom: '1px solid var(--border2)', borderRadius: 0, cursor: 'pointer' }}>
            <div className="grow"><div>{c.name}</div></div>
            <span style={{ color: 'var(--text3)' }}>›</span>
          </button>
        ))}
      </div>

      {/* Accounts */}
      <div className="row between" style={{ marginTop: 24, marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Accounts</h2>
        <button onClick={() => setEditingAcct('new')}>+ New</button>
      </div>
      <div className="card" style={{ padding: 0 }}>
        {accts.map(a => (
          <button key={a.id} onClick={() => setEditingAcct(a)} className="list-item"
                  style={{ width: '100%', textAlign: 'left', background: 'transparent', border: 'none',
                           borderBottom: '1px solid var(--border2)', borderRadius: 0, cursor: 'pointer' }}>
            <div className="grow">
              <div>{a.name}</div>
              <div className="meta">{a.kind}</div>
            </div>
            <span style={{ color: 'var(--text3)' }}>›</span>
          </button>
        ))}
      </div>

      <CategoryEditor editing={editingCat} onClose={() => setEditingCat(null)}
                      generic={generic} showToast={showToast} />
      <AccountEditor editing={editingAcct} onClose={() => setEditingAcct(null)}
                     generic={generic} showToast={showToast} />
    </div>
  );
}

function ReconcileChecking({ state, mutate, reload, showToast }) {
  const ask = useConfirm();
  const [bankInput, setBankInput] = useState('');
  const [posting, setPosting] = useState(false);

  // Resolve Checking account by name — same convention the Dashboard uses.
  const checking = state.accounts.find(a => a.name === 'Checking' && !a.archived);

  // Live ledger balance: sum amount_cents for every Checking transaction.
  // Use allTransactions if loaded; otherwise fall back to the bootstrap recents.
  // (This card lives on Settings, which doesn't auto-load full history, so
  // for a brand-new session the figure may be slightly off until full
  // history loads on Dashboard. We display a hint to that effect when the
  // full ledger isn't loaded.)
  const txns = state.allTransactions ?? state.transactions ?? [];
  const fullLoaded = state.allTransactions != null;
  const ledgerCents = checking
    ? txns.filter(t => t.account_id === checking.id)
          .reduce((sum, t) => sum + (t.amount_cents ?? 0), 0)
    : 0;

  const bankCents = parseAmountToCents(bankInput);
  const validBank = bankInput.trim() !== '' && bankCents != null;
  const deltaCents = validBank ? (bankCents - ledgerCents) : 0;

  async function reconcile() {
    if (!checking) return showToast({ kind: 'error', message: 'No "Checking" account found.' });
    if (!validBank) return showToast({ kind: 'error', message: 'Enter the bank balance first.' });
    if (deltaCents === 0) return showToast({ kind: 'success', message: 'Already in sync — no entry needed.' });

    const sign = deltaCents > 0 ? '+' : '−';
    const ok = await ask({
      title: 'Post reconciliation entry?',
      message:
        `Ledger: ${fmtCents(ledgerCents)}\n` +
        `Bank:   ${fmtCents(bankCents)}\n` +
        `Adjust: ${sign}${fmtCents(Math.abs(deltaCents))}\n\n` +
        `This adds ONE ${deltaCents > 0 ? 'Income' : 'Expense'} transaction on today's date in the "Reconciliation" category. ` +
        `Your existing transactions are untouched. To undo, just delete the new transaction.`,
      confirmLabel: 'Post entry',
    });
    if (!ok) return;

    setPosting(true);
    try {
      // Find or create a "Reconciliation" category. Categories don't have
      // a type column; we just keep the convention by name.
      let cat = state.categories.find(c => c.name === 'Reconciliation' && !c.archived);
      if (!cat) {
        const maxSort = state.categories.reduce((m, c) => Math.max(m, c.sort_order ?? 0), 0);
        cat = await api.createCategory({ name: 'Reconciliation', sort_order: maxSort + 1 });
      }

      await mutate.addTransaction({
        occurred_on: todayIso(),
        description: deltaCents > 0 ? 'Reconciliation (credit)' : 'Reconciliation (debit)',
        category_id: cat.id,
        type: deltaCents > 0 ? 'Income' : 'Expense',
        account_id: checking.id,
        amount_cents: deltaCents, // already signed: positive=credit, negative=debit
        notes: `Reconciled to bank balance ${fmtCents(bankCents)} on ${todayIso()}. ` +
               `Pre-reconciliation ledger was ${fmtCents(ledgerCents)}.`,
      });
      await reload();
      setBankInput('');
      showToast({ kind: 'success', message: `Posted ${sign}${fmtCents(Math.abs(deltaCents))}.` });
    } catch (e) {
      showToast({ kind: 'error', message: 'Failed: ' + e.message });
    } finally {
      setPosting(false);
    }
  }

  if (!checking) {
    return <div className="muted tiny">No "Checking" account found.</div>;
  }

  const deltaTone = deltaCents === 0 ? 'muted' : (deltaCents > 0 ? 'pos' : 'neg');

  return (
    <div className="stack" style={{ gap: 12 }}>
      <div className="row between">
        <span className="muted tiny" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Ledger says
        </span>
        <span className="amount" style={{ fontSize: 16 }}>{fmtCents(ledgerCents)}</span>
      </div>

      <div className="stack" style={{ gap: 4 }}>
        <label className="muted tiny" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Bank balance
        </label>
        <input
          inputMode="decimal"
          placeholder="0.00"
          value={bankInput}
          onChange={(e) => setBankInput(e.target.value)}
        />
      </div>

      <div className="row between">
        <span className="muted tiny" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Difference
        </span>
        <span className={'amount ' + (deltaCents > 0 ? 'pos' : deltaCents < 0 ? 'neg' : '')}>
          {validBank
            ? (deltaCents === 0 ? fmtCents(0) : (deltaCents > 0 ? '+' : '−') + fmtCents(Math.abs(deltaCents)))
            : '—'}
        </span>
      </div>

      <button
        className="primary"
        onClick={reconcile}
        disabled={posting || !validBank || deltaCents === 0}
      >
        {posting ? <span className="spinner" /> : 'Post reconciliation entry'}
      </button>

      <div className="tiny muted" style={{ lineHeight: 1.45 }}>
        Adds one {deltaCents > 0 ? 'Income' : deltaCents < 0 ? 'Expense' : 'adjustment'} transaction
        on today's date in the <b>Reconciliation</b> category. Your history stays intact —
        delete the entry from Activity if you ever need to undo it.
        {!fullLoaded && <> · <span style={{ color: 'var(--warn)' }}>Visit the Home tab first to load your full ledger for an accurate ledger figure.</span></>}
      </div>
    </div>
  );
}


function SettingRow({ label, value, editValue, parse, onSave, saving, hint, last }) {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(editValue);

  async function save() {
    const out = parse ? parse(v) : v;
    await onSave(out);
    setEditing(false);
  }

  return (
    <div className="list-item" style={{
      borderBottom: last ? 'none' : '1px solid var(--border2)',
      flexDirection: 'column', alignItems: 'stretch', gap: 6,
    }}>
      <div className="row between">
        <span className="muted tiny" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</span>
        {!editing && <button className="ghost" onClick={() => { setV(editValue); setEditing(true); }}>Edit</button>}
      </div>
      {editing ? (
        <div className="row" style={{ gap: 8 }}>
          <input value={v} onChange={(e) => setV(e.target.value)} autoFocus />
          <button onClick={() => setEditing(false)}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>{saving ? <span className="spinner"/> : 'Save'}</button>
        </div>
      ) : (
        <div>
          <div className="amount" style={{ fontSize: 16 }}>{value}</div>
          {hint && <div className="tiny muted" style={{ marginTop: 4 }}>{hint}</div>}
        </div>
      )}
    </div>
  );
}

function CategoryEditor({ editing, onClose, generic, showToast }) {
  if (!editing) return null;
  const isNew = editing === 'new';
  const initial = isNew ? { name: '', sort_order: 0 } : editing;
  return (
    <Modal open={!!editing} onClose={onClose} title={isNew ? 'New Category' : 'Edit Category'}>
      <SimpleNameForm key={isNew ? 'new' : editing.id} initial={initial} isNew={isNew}
                      table="categories" generic={generic} showToast={showToast} onClose={onClose} />
    </Modal>
  );
}

function AccountEditor({ editing, onClose, generic, showToast }) {
  if (!editing) return null;
  const isNew = editing === 'new';
  const initial = isNew ? { name: '', kind: 'checking', sort_order: 0 } : editing;
  return (
    <Modal open={!!editing} onClose={onClose} title={isNew ? 'New Account' : 'Edit Account'}>
      <SimpleAccountForm key={isNew ? 'new' : editing.id} initial={initial} isNew={isNew}
                         generic={generic} showToast={showToast} onClose={onClose} />
    </Modal>
  );
}

function SimpleNameForm({ initial, isNew, table, generic, showToast, onClose }) {
  const ask = useConfirm();
  const [form, setForm] = useState({ name: initial.name ?? '', sort_order: initial.sort_order ?? 0 });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.name) return showToast({ kind: 'error', message: 'Name required.' });
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), sort_order: parseInt(form.sort_order, 10) || 0 };
      if (isNew) await generic[table].create(payload);
      else await generic[table].update(initial.id, payload);
      showToast({ kind: 'success', message: 'Saved.' });
      onClose();
    } catch (e) { showToast({ kind: 'error', message: 'Failed: ' + e.message }); }
    finally { setSaving(false); }
  }

  async function archive() {
    const ok = await ask({ title: 'Archive category?', message: 'It will be hidden but past transactions stay.', confirmLabel: 'Archive', danger: true }); if (!ok) return;
    setSaving(true);
    try {
      await generic[table].remove(initial.id);
      showToast({ kind: 'success', message: 'Archived.' });
      onClose();
    } catch (e) { showToast({ kind: 'error', message: 'Failed: ' + e.message }); }
    finally { setSaving(false); }
  }

  return (
    <>
      <div className="field"><label>Name</label>
        <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} autoFocus /></div>
      <div className="field"><label>Sort Order</label>
        <input type="number" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))} /></div>
      <div className="row between" style={{ marginTop: 24 }}>
        {!isNew ? <button className="danger" onClick={archive} disabled={saving}>Archive</button> : <span />}
        <div className="row" style={{ gap: 8 }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>{saving ? <span className="spinner"/> : 'Save'}</button>
        </div>
      </div>
    </>
  );
}

function SimpleAccountForm({ initial, isNew, generic, showToast, onClose }) {
  const ask = useConfirm();
  const [form, setForm] = useState({
    name: initial.name ?? '',
    kind: initial.kind ?? 'checking',
    sort_order: initial.sort_order ?? 0,
  });
  const [saving, setSaving] = useState(false);

  async function save() {
    if (!form.name) return showToast({ kind: 'error', message: 'Name required.' });
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), kind: form.kind, sort_order: parseInt(form.sort_order, 10) || 0 };
      if (isNew) await generic.accounts.create(payload);
      else await generic.accounts.update(initial.id, payload);
      showToast({ kind: 'success', message: 'Saved.' });
      onClose();
    } catch (e) { showToast({ kind: 'error', message: 'Failed: ' + e.message }); }
    finally { setSaving(false); }
  }

  async function archive() {
    const ok = await ask({ title: 'Archive account?', message: 'It will be hidden but past transactions stay.', confirmLabel: 'Archive', danger: true }); if (!ok) return;
    setSaving(true);
    try {
      await generic.accounts.remove(initial.id);
      showToast({ kind: 'success', message: 'Archived.' });
      onClose();
    } catch (e) { showToast({ kind: 'error', message: 'Failed: ' + e.message }); }
    finally { setSaving(false); }
  }

  return (
    <>
      <div className="field"><label>Name</label>
        <input value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} autoFocus /></div>
      <div className="field"><label>Kind</label>
        <select value={form.kind} onChange={(e) => setForm(f => ({ ...f, kind: e.target.value }))}>
          <option>checking</option>
          <option>savings</option>
          <option>credit</option>
          <option>brokerage</option>
          <option>retirement</option>
          <option>cash</option>
          <option>other</option>
        </select></div>
      <div className="field"><label>Sort Order</label>
        <input type="number" value={form.sort_order} onChange={(e) => setForm(f => ({ ...f, sort_order: e.target.value }))} /></div>
      <div className="row between" style={{ marginTop: 24 }}>
        {!isNew ? <button className="danger" onClick={archive} disabled={saving}>Archive</button> : <span />}
        <div className="row" style={{ gap: 8 }}>
          <button onClick={onClose}>Cancel</button>
          <button className="primary" onClick={save} disabled={saving}>{saving ? <span className="spinner"/> : 'Save'}</button>
        </div>
      </div>
    </>
  );
}
